import { Download, ExternalLink, Share2, X } from "lucide-react";
import { useEffect, useState } from "react";
import type { Language } from "../i18n";
import type { GeneratedFile } from "../types";
import { canShareFile, prepareGeneratedFile, releaseDownloadUrl } from "../lib/file-save";
import { ui } from "../lib/localization";
import { isDialogBackdropClick, useModalDialog } from "../lib/use-modal-dialog";

export function FileSaveDialog({ file, language, onClose }: { file: GeneratedFile; language: Language; onClose: () => void }) {
  const dialogRef = useModalDialog(true);
  const [prepared, setPrepared] = useState<{ file: File; url: string; shareable: boolean }>();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<"ready" | "sharing" | "shared" | "cancelled" | "share-error" | "download" | "opened">("ready");

  useEffect(() => {
    let disposed = false;
    let objectUrl: string | undefined;
    setPrepared(undefined); setFailed(false); setStatus("ready");
    void prepareGeneratedFile(file).then((result) => {
      if (disposed) return;
      objectUrl = URL.createObjectURL(result);
      setPrepared({ file: result, url: objectUrl, shareable: canShareFile(result) });
    }).catch(() => { if (!disposed) setFailed(true); });
    return () => { disposed = true; if (objectUrl) releaseDownloadUrl(objectUrl); };
  }, [file, attempt]);

  const share = async () => {
    if (!prepared || status === "sharing") return;
    setStatus("sharing");
    try {
      // Do not await any preparation before this call: it must run in the tap handler.
      await navigator.share({ files: [prepared.file] });
      setStatus("shared");
    } catch (error) {
      setStatus(error && typeof error === "object" && "name" in error && error.name === "AbortError" ? "cancelled" : "share-error");
    }
  };

  const message = failed ? ui(language, "File could not be prepared. The link may have expired or the connection failed. Try again.")
    : !prepared ? ui(language, "Preparing file…")
    : status === "sharing" ? ui(language, "Choose where to save in the system panel.")
    : status === "shared" ? ui(language, "System panel closed. If you saved the file, find it in the folder you selected.")
    : status === "cancelled" ? ui(language, "Save cancelled. You can try again.")
    : status === "share-error" ? ui(language, "Could not open the system save panel. Try again or use browser download.")
    : status === "download" ? ui(language, "Download requested. Check your browser's downloads; this page cannot confirm that the file was saved.")
    : status === "opened" ? ui(language, "File opened in a new tab. In Safari, use Share → Save to Files.")
    : ui(language, "File ready. Choose how to save it.");

  return <dialog ref={dialogRef} className="file-save-dialog" aria-label={ui(language, "Save file")}
    onCancel={onClose} onClick={(event) => { if (isDialogBackdropClick(event)) onClose(); }}>
    <header><h2>{ui(language, "Save file")}</h2><button type="button" className="file-save-close" onClick={onClose} aria-label={ui(language, "Close")}><X size={19} /></button></header>
    <strong className="file-save-name">{file.fileName}</strong>
    <p role={failed || status === "share-error" ? "alert" : "status"} aria-live="polite" className={failed || status === "share-error" ? "file-save-error" : ""}>{message}</p>
    {prepared && <>
      {prepared.shareable && <p className="file-save-hint">{ui(language, "On iPhone, choose Save to Files in the system panel, select a folder, then tap Save.")}</p>}
      <div className="file-save-actions">
        {prepared.shareable && <button type="button" className="file-save-primary" onClick={() => void share()} disabled={status === "sharing"}><Share2 size={18} />{ui(language, "Save or share…")}</button>}
        <a href={prepared.url} download={file.fileName} onClick={() => setStatus("download")}><Download size={18} />{ui(language, "Browser download")}</a>
        <a href={prepared.url} target="_blank" rel="noopener noreferrer" onClick={() => setStatus("opened")}><ExternalLink size={18} />{ui(language, "Open file")}</a>
      </div>
      <p className="file-save-hint">{ui(language, "On iPhone, browser downloads are in Files → Browse → Downloads, under iCloud Drive or On My iPhone, depending on Safari settings.")}</p>
    </>}
    {failed && <div className="file-save-actions"><button type="button" onClick={() => setAttempt((value) => value + 1)}>{ui(language, "Try again")}</button></div>}
  </dialog>;
}
